import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API_ENDPOINTS } from "../../api/config";
import JSLayout from "./js_layout";
import './css/job_seeker_dashboard.css';

// Status styling configuration mapping rules
const statusClass = { 
  applied: "status-applied", 
  review: "status-review", 
  interview: "status-interview", 
  offered: "status-offered", 
  rejected: "status-rejected" 
};

// Compact stepper configuration settings
const DASHBOARD_STEPS = ["Applied", "Review", "Interview", "Offer"];
const STATUS_INDEXES = { applied: 0, review: 1, interview: 2, offered: 3, rejected: -1 };

export default function JobSeekerDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      const token = localStorage.getItem("token");
      try {
        const response = await fetch(API_ENDPOINTS.SEEKERDASHBOARD, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        if (response.ok) {
          const result = await response.json();
          setData(result);
        }
      } catch (err) {
        console.error("Dashboard error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const handleToggleSave = async (jobId) => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(`${API_ENDPOINTS.EMPJOBS}/${jobId}/save`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        const updatedJobs = data.savedJobs.filter(j => j.id !== jobId);
        setData({ ...data, savedJobs: updatedJobs });
      }
    } catch (err) {
      console.error("Error toggling save:", err);
    }
  };

  if (loading) return <JSLayout title="Loading..."><div>Syncing your profile...</div></JSLayout>;

  return (
    <JSLayout title="Good morning," titleEm={`${data?.firstName || 'User'} ✦`}>
      <div className="jsd-fade">

        {/* 1. Profile Completion Banner */}
        {data?.profileCompletionPercentage < 100 ?(
        <div className="jsd-profile-banner">
          <div className="jsd-banner-text">
            <div className="jsd-banner-title">
              {data?.profileCompletionPercentage < 100 ? "Complete your profile" : "Profile looking sharp!"}
            </div>
            <div className="jsd-banner-sub">
              {data?.profileCompletionPercentage < 100 
                ? "Complete profiles get 3x more interview invites." 
                : "Keep your details updated to stay relevant."}
            </div>
          </div>
          <div className="jsd-progress-wrap">
            <div className="jsd-progress-bg">
              <div className="jsd-progress-fill" style={{ width: `${data?.profileCompletionPercentage || 0}%` }} />
            </div>
            <div className="jsd-progress-label">{data?.profileCompletionPercentage || 0}%</div>
          </div>
          <button className="jsd-banner-btn" onClick={() => navigate("/profile")}>
            {data?.profileCompletionPercentage < 100 ? "Update Profile" : "Edit Details"}
          </button>
        </div>
        ) : null}

        <div className="jsd-grid3">
          {/* 2. Saved Jobs Section */}
          <div className="jsd-main-col">
            <div className="jsd-section-hdr">
              <div className="jsd-section-title">Saved <em>jobs</em></div>
              {data?.savedJobs?.length > 0 && <Link to="/saved-jobs" className="jsd-view-all">View all →</Link>}
            </div>
            
            <div className="jsd-jobs-list">
              {data?.savedJobs?.length > 0 ? (
                data.savedJobs.map(job => (
                  <div key={job.id} className="jsd-job-card" onClick={() => navigate(`/jobs/${job.id}`)}>
                    <div className="jsd-job-info">
                      <div className="jsd-job-title">{job.title}</div>
                      <div className="jsd-job-company">{job.company}</div>
                      <div className="jsd-job-meta">
                        <span>📍 {job.location}</span>
                        <span className="jsd-dot">•</span>
                        <span>{job.salaryRange}</span>
                      </div>
                    </div>
                    <div className="jsd-job-right">
                      <button className="jsd-save-btn saved" onClick={(e) => { 
                        e.stopPropagation(); 
                        handleToggleSave(job.id); 
                      }}>
                        ♥
                      </button>
                      <div className="jsd-view-link">View Details</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="emd-empty-container">
                  <div className="emd-empty-content">
                    <div className="emd-empty-icon">🔖</div>
                    <p className="emd-empty-title">Your shortlist is empty</p>
                    <p className="emd-empty-text">Save jobs you're interested in to keep track of them here.</p>
                    <Link to="/jobs" className="jsd-banner-btn" style={{ textDecoration: 'none'}}>
                      Explore Job Board
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 3. Skills Sidebar */}
          <div className="jsd-side-col">
            <div className="jsd-panel-card">
              <div className="jsd-panel-title">My <em>skills</em></div>
              <div className="jsd-skill-tags">
                {data?.skills?.length > 0 ? (
                  <>
                    {data.skills.map(skill => (
                      <span key={skill} className="jsd-skill-tag">{skill}</span>
                    ))}
                    <button className="jsd-add-skill" onClick={() => navigate("/profile")}>+</button>
                  </>
                ) : (
                  <div className="jsd-empty-skills">
                    <p>No skills added yet.</p>
                    <button className="jsd-banner-btn" onClick={() => navigate("/profile")}>Add Skills</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 4. IMPROVED APPLICATIONS SECTION */}
        <div className="jsd-section-hdr" style={{ marginTop: "2.5rem" }}>
          <div className="jsd-section-title">My <em>applications</em></div>
          {data?.recentApplications?.length > 0 && <Link to="/applications" className="jsd-view-all">Manage →</Link>}
        </div>

        <div className="jsd-apps-list-clean" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {data?.recentApplications?.length > 0 ? (
            data.recentApplications.map(app => {
              const currentStatus = (app.status || "applied").toLowerCase().trim();

              return (
                <div 
                  key={app.id} 
                  className="jsd-app-row-clean" 
                  onClick={() => navigate(`/jobs/${app.jobListingId || app.id}`)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '1rem 1.25rem', background: '#fff', border: '1px solid #eee', 
                    borderRadius: '6px', cursor: 'pointer', transition: 'all 0.2s ease'
                  }}
                >
                  {/* Left Column: Context Branding & Text Block */}
                  <div style={{ display: 'flex', alignmentItems: 'center', gap: '1rem' }}>
                    <div style={{ fontSize: '1.25rem', opacity: 0.85 }}>💼</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1a1a1a' }}>{app.jobTitle}</div>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>
                        {app.companyName} <span style={{ color: '#ccc', margin: '0 4px' }}>|</span> 📍 {app.location || 'Nairobi'}
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Status & Date Meta indicators */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                    {app.dateApplied && (
                      <span style={{ fontSize: '0.8rem', color: '#999', display: 'none', mdDisplay: 'inline' }} className="hide-on-mobile">
                        Applied {app.dateApplied}
                      </span>
                    )}
                    <span className={`bl-status-pill ${statusClass[currentStatus] || 'status-applied'}`}>
                      {app.statusLabel || 'Applied'}
                    </span>
                    <span style={{ color: '#ccc', fontSize: '0.85rem' }}>→</span>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="emd-empty-container">
              <div className="emd-empty-content">
                <div className="emd-empty-icon">📝</div>
                <p className="emd-empty-title">Ready to start your journey?</p>
                <p className="emd-empty-text">Your active applications will appear here once you apply for jobs.</p>
                <Link to="/jobs" className="jsd-banner-btn" style={{ textDecoration: 'none', display: 'inline-block'}}>
                  Browse Jobs
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </JSLayout>
  );
}